import React, { useEffect, useState } from "react";
import { Loader2, Check, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, isValidEmail } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const fmtDate = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "long" }); }
  catch { return d; }
};

export const BookingDialog = ({ open, onOpenChange, listingId }) => {
  const [slots, setSlots] = useState(null);
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", consent: false, budget: "", financing: "", own_home_to_sell: "", buying_agent: "", move_timeline: "" });

  useEffect(() => {
    if (!open) return;
    setStep(1); setSelected(null); setSlots(null);
    api(`/koop/listings/${listingId}/slots`)
      .then((d) => setSlots(d))
      .catch(() => setSlots({ slots: [], viewings_allowed: true }));
  }, [open, listingId]);

  const book = async () => {
    if (!form.name.trim() || !isValidEmail(form.email) || !form.phone.trim()) return toast.error("Vul naam, geldig e-mail en telefoon in.");
    if (!form.consent) return toast.error("Ga akkoord met de privacyverklaring.");
    setBusy(true);
    try {
      await api(`/koop/listings/${listingId}/book`, {
        method: "POST",
        body: JSON.stringify({
          slot_id: selected.id, name: form.name, email: form.email, phone: form.phone, consent: true,
          budget: form.budget ? Number(form.budget) : null,
          financing: form.financing || null,
          own_home_to_sell: form.own_home_to_sell === "" ? null : form.own_home_to_sell === "ja",
          buying_agent: form.buying_agent === "" ? null : form.buying_agent === "ja",
          move_timeline: form.move_timeline || null,
        }),
      });
      setStep(3);
    } catch (e) {
      if (e.status === 409) {
        toast.error("Dit tijdslot is net volgeboekt. Kies een ander moment.");
        setStep(1); setSelected(null);
        api(`/koop/listings/${listingId}/slots`).then(setSlots).catch(() => {});
      } else { toast.error(e.message); }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Plan een bezichtiging</DialogTitle>
          <DialogDescription>Kies een beschikbaar moment. Je ontvangt een bevestiging per e-mail.</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          slots === null ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-brandblue" /></div>
          ) : !slots.viewings_allowed ? (
            <p className="py-6 text-center text-sm text-slate-500">Voor deze woning zijn bezichtigingen momenteel niet mogelijk. Toon liever je interesse.</p>
          ) : slots.slots.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500" data-testid="no-slots">Er zijn nog geen bezichtigingsmomenten gepland. Toon je interesse en de makelaar neemt contact op.</p>
          ) : (
            <div className="space-y-2">
              <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                {slots.slots.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`slot-${s.id}`}
                    onClick={() => setSelected(s)}
                    className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition-colors ${selected?.id === s.id ? "border-brandblue bg-brandblue-light" : "border-border hover:border-brandblue/50"}`}
                  >
                    <span className="flex items-center gap-2 font-semibold text-navy"><CalendarDays size={15} /> {fmtDate(s.date)}</span>
                    <span className="text-slate-600">{s.start_time}–{s.end_time}{s.slot_type === "open_huis" ? " · Open huis" : ""}</span>
                  </button>
                ))}
              </div>
              <Button data-testid="slot-continue" disabled={!selected} onClick={() => setStep(2)} className="w-full rounded-xl bg-brandblue py-6 hover:bg-brandblue/90">Verder</Button>
            </div>
          )
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="rounded-xl bg-slate-50 px-4 py-2 text-sm font-semibold text-navy">{fmtDate(selected.date)} · {selected.start_time}–{selected.end_time}</div>
            <div className="grid grid-cols-1 gap-3">
              <div><Label>Naam</Label><Input data-testid="book-name" className="mt-1.5 rounded-xl" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>E-mail</Label><Input data-testid="book-email" type="email" className="mt-1.5 rounded-xl" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
                <div><Label>Telefoon</Label><Input data-testid="book-phone" className="mt-1.5 rounded-xl" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
              </div>
            </div>
            <details className="rounded-xl border border-border px-4 py-2 text-sm">
              <summary className="cursor-pointer font-semibold text-slate-600">Extra info (optioneel — helpt de makelaar)</summary>
              <div className="mt-3 space-y-3">
                <div><Label>Indicatie koopbudget (€)</Label><Input type="number" className="mt-1.5 rounded-xl" value={form.budget} onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))} /></div>
                <div><Label>Financiering al besproken?</Label>
                  <Select value={form.financing} onValueChange={(v) => setForm((f) => ({ ...f, financing: v }))}>
                    <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue placeholder="Kies…" /></SelectTrigger>
                    <SelectContent><SelectItem value="ja">Ja</SelectItem><SelectItem value="nee">Nee</SelectItem><SelectItem value="gedeeltelijk">Gedeeltelijk</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Eigen woning te verkopen?</Label>
                  <Select value={form.own_home_to_sell} onValueChange={(v) => setForm((f) => ({ ...f, own_home_to_sell: v }))}>
                    <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue placeholder="Kies…" /></SelectTrigger>
                    <SelectContent><SelectItem value="ja">Ja</SelectItem><SelectItem value="nee">Nee</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </details>
            <label className="flex items-start gap-2 text-xs text-slate-500">
              <input data-testid="book-consent" type="checkbox" className="mt-0.5" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} />
              <span>Ik geef toestemming om contact met mij op te nemen over deze bezichtiging (<a href="/privacy" target="_blank" rel="noreferrer" className="text-brandblue underline">privacy</a>).</span>
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl">Terug</Button>
              <Button data-testid="book-confirm" onClick={book} disabled={busy} className="flex-1 rounded-xl bg-brandblue hover:bg-brandblue/90">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "Bevestig bezichtiging"}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="py-6 text-center" data-testid="booking-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success"><Check size={28} /></div>
            <h3 className="mt-4 font-display text-lg font-bold text-navy">Bezichtiging aangevraagd!</h3>
            <p className="mt-1 text-sm text-slate-500">Je ontvangt een bevestiging per e-mail. Je kunt de afspraak later verplaatsen of annuleren.</p>
            <Button onClick={() => onOpenChange(false)} className="mt-5 rounded-xl bg-brandblue px-8 hover:bg-brandblue/90">Sluiten</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
